import React, { useState } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { BiEuro } from 'react-icons/bi';
import { LuRefreshCw } from 'react-icons/lu';
import { propTypes } from '../../util/types';
import { maxLength, required, requiredRichText, composeValidators } from '../../util/validators';
import {
  Form,
  Button,
  SecondaryButton,
  FieldTextInput,
  FieldTextEditor,
  FieldSelectModern,
  IconSpinner,
  UnsavedInfoPrompt,
} from '../../components';
import { STEPS as BUDGET_STEPS } from '../EditProjectListingBudgetForm/EditProjectListingBudgetForm';

import css from './EditProjectListingDraftForm.css';

const TITLE_MAX_LENGTH = 80;
// Plain-text minimum for the description; its error waits until the editor has
// lost focus or a publish has been tried (see FieldTextEditor's minLength).
export const DESCRIPTION_MIN_LENGTH = 200;

// Short labels for the editable budget/scope dropdowns shown at the top of the
// form. The full BUDGET_STEPS titleId strings are sentences; these are concise.
const BUDGET_FIELD_LABELS = {
  projectDuration: 'EditProjectListingDraftForm.fieldCollaboration',
  projectUrgency: 'EditProjectListingDraftForm.fieldUrgency',
  priceEstimate: 'EditProjectListingDraftForm.fieldBudget',
};
const BUDGET_FIELD_REQUIRED = {
  projectDuration: 'EditProjectListingDraftForm.collaborationRequired',
  projectUrgency: 'EditProjectListingDraftForm.urgencyRequired',
  priceEstimate: 'EditProjectListingDraftForm.budgetRequired',
};

const EditProjectListingDraftFormComponent = props => {
  // A successful draft save leaves the page, so the leave warning stands down
  // while the save is on its way and comes back if it fails.
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  return (
    <FinalForm
      {...props}
      render={formRenderProps => {
        const {
          className,
          disabled,
          ready,
          handleSubmit,
          intl,
          pristine,
          values,
          saveActionMsg,
          saveDraftActionMsg,
          saveDraftInProgress,
          onSaveDraft,
          updated,
          updateInProgress,
          fetchErrors,
          dirty,
          isGenerating,
        } = formRenderProps;

        const titleLabel = intl.formatMessage({ id: 'EditProjectListingDraftForm.titleLabel' });
        const titlePlaceholder = intl.formatMessage({
          id: 'EditProjectListingDraftForm.titlePlaceholder',
        });
        const titleRequired = intl.formatMessage({
          id: 'EditProjectListingDraftForm.titleRequired',
        });
        const titleMax = maxLength(
          intl.formatMessage(
            { id: 'EditProjectListingDraftForm.titleMax' },
            { maxLength: TITLE_MAX_LENGTH }
          ),
          TITLE_MAX_LENGTH
        );

        const descriptionLabel = intl.formatMessage({
          id: 'EditProjectListingDraftForm.descriptionLabel',
        });
        const descriptionPlaceholder = intl.formatMessage({
          id: 'EditProjectListingDraftForm.descriptionPlaceholder',
        });
        const descriptionRequired = intl.formatMessage({
          id: 'EditProjectListingDraftForm.descriptionRequired',
        });
        const descriptionTooShort = intl.formatMessage(
          { id: 'EditProjectListingDraftForm.descriptionMin' },
          { minLength: DESCRIPTION_MIN_LENGTH }
        );

        const { updateListingError, createListingDraftError, saveListingDraftError } =
          fetchErrors || {};
        const errorBanner = (updateListingError ||
          createListingDraftError ||
          saveListingDraftError) && (
          <p className={css.error}>
            <FormattedMessage id="EditProjectListingDraftForm.submitFailed" />
          </p>
        );

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        // Publish stays clickable while the form is invalid: a click on it then
        // reveals every field's error (Final Form marks them submitFailed)
        // instead of a greyed-out button with no reason given.
        const submitDisabled = disabled || submitInProgress || isGenerating;

        // While the writer agent streams, show a loading indicator in place of the
        // title and description fields. The selections stay visible (disabled).
        const writingIndicator = (
          <div className={css.fieldLoading}>
            <IconSpinner />
            <span>
              <FormattedMessage id="EditProjectListingDraftForm.writing" />
            </span>
          </div>
        );

        // Informational pill next to the budget select; copy + icon depend on the
        // chosen collaboration length. Continuous → monthly estimate; project → total.
        const projectDuration = values?.projectDuration;
        const budgetHint =
          projectDuration === 'continuous' || projectDuration === 'project' ? (
            <div className={css.budgetHintCell}>
              <span className={css.budgetHint}>
                {projectDuration === 'continuous' ? (
                  <LuRefreshCw
                    className={classNames(css.budgetHintIcon, css.budgetHintIconRefresh)}
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                ) : (
                  <BiEuro
                    className={classNames(css.budgetHintIcon, css.budgetHintIconEuro)}
                    aria-hidden="true"
                  />
                )}
                <FormattedMessage
                  id={
                    projectDuration === 'continuous'
                      ? 'EditProjectListingDraftForm.budgetHintContinuous'
                      : 'EditProjectListingDraftForm.budgetHintProject'
                  }
                />
              </span>
            </div>
          ) : null;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {errorBanner}

            {/* Listing details — editable budget / scope selections */}
            <h2 className={css.cardHeading}>
              <FormattedMessage id="EditProjectListingDraftForm.detailsHeading" />
            </h2>
            <div className={css.detailsCard}>
              <div className={css.budgetRow}>
                {BUDGET_STEPS.map(step => {
                  // react-select works with { value, label } option objects, but the
                  // listing stores these as raw strings. format/parse bridge the two so
                  // the form value (and the panel's update payload) stays a plain string.
                  const options = step.options.map(opt => ({
                    value: opt.value,
                    label: opt.labelId ? intl.formatMessage({ id: opt.labelId }) : opt.label,
                  }));
                  const label = intl.formatMessage({ id: BUDGET_FIELD_LABELS[step.field] });
                  const requiredMsg = intl.formatMessage({ id: BUDGET_FIELD_REQUIRED[step.field] });
                  return (
                    <FieldSelectModern
                      key={step.field}
                      id={step.field}
                      name={step.field}
                      className={css.budgetField}
                      label={label}
                      placeholder={label}
                      options={options}
                      isSearchable={false}
                      isClearable={false}
                      disabled={isGenerating}
                      format={value => options.find(opt => opt.value === value) || null}
                      parse={option => (option ? option.value : undefined)}
                      validate={required(requiredMsg)}
                    />
                  );
                })}
                {budgetHint}
              </div>
            </div>

            {/* Editable title + description */}
            <div className={css.editorCard}>
              <label className={css.fieldLabel} htmlFor="title">
                {titleLabel}
              </label>
              {isGenerating ? (
                writingIndicator
              ) : (
                <FieldTextInput
                  id="title"
                  name="title"
                  type="text"
                  placeholder={titlePlaceholder}
                  maxLength={TITLE_MAX_LENGTH}
                  validate={composeValidators(required(titleRequired), titleMax)}
                />
              )}

              <label
                className={classNames(css.fieldLabel, css.descriptionLabel)}
                htmlFor="descriptionText"
              >
                {descriptionLabel}
              </label>
              {isGenerating ? (
                writingIndicator
              ) : (
                <FieldTextEditor
                  id="descriptionText"
                  name="descriptionText"
                  type="textarea"
                  className={css.descriptionEditor}
                  quillClass={css.descriptionQuill}
                  placeholder={descriptionPlaceholder}
                  isJobPostingDescription
                  disableLinkValidation
                  minLength={DESCRIPTION_MIN_LENGTH}
                  minLengthMessage={descriptionTooShort}
                  validate={requiredRichText(descriptionRequired)}
                />
              )}
            </div>

            <div className={css.actions}>
              {onSaveDraft ? (
                <SecondaryButton
                  type="button"
                  className={css.saveDraftButton}
                  inProgress={saveDraftInProgress}
                  disabled={isGenerating || disabled || saveDraftInProgress}
                  onClick={() => {
                    setIsSavingDraft(true);
                    Promise.resolve(onSaveDraft(values)).catch(() => setIsSavingDraft(false));
                  }}
                >
                  {saveDraftActionMsg}
                </SecondaryButton>
              ) : null}
              <Button
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                {saveActionMsg}
              </Button>
            </div>
            <UnsavedInfoPrompt when={dirty && !isSavingDraft} />
          </Form>
        );
      }}
    />
  );
};

EditProjectListingDraftFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  isGenerating: false,
  onSaveDraft: null,
  saveDraftActionMsg: null,
  saveDraftInProgress: false,
};

EditProjectListingDraftFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  saveDraftActionMsg: string,
  saveDraftInProgress: bool,
  onSaveDraft: func,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  isGenerating: bool,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    updateListingError: propTypes.error,
    saveListingDraftError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditProjectListingDraftFormComponent);
